import { User, Upload, X, Loader2 } from 'lucide-react';
import React, { useState, useCallback, useEffect } from 'react';
import type { User as SupabaseUser } from '@supabase/supabase-js';

import { supabase } from '../../../lib/supabase';
import { useStudentOnboardingStore } from '../../../store/studentOnboarding.store';
import { Button } from '../../../components/ui/Button';

interface AvatarStepProps {
  onNext: () => void;
}

export function AvatarStep({ onNext }: AvatarStepProps) {
  const { profileData, setProfileData, setAvatarSet } = useStudentOnboardingStore();
  const [avatarUrl, setAvatarUrl] = useState(profileData.avatarUrl || '');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [user, setUser] = useState<SupabaseUser | null>(null);

  useEffect(() => {
    const loadUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setUser(session.user);
        // Load existing avatar if available
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('avatar_url')
          .eq('id', session.user.id)
          .single();

        if (profile?.avatar_url) {
          setAvatarUrl(profile.avatar_url);
        }
      }
    };
    loadUser();
  }, []);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const uploadFile = useCallback(async (file: File) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file (PNG, JPG, etc.)');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      return;
    }

    if (!user) {
      setError('Please wait for authentication to complete.');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      // Ensure we have a valid session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session?.user) {
        setError('Please wait for authentication to complete. If this persists, please refresh the page.');
        setUploading(false);
        return;
      }

      // Verify user ID matches session
      if (user.id !== session.user.id) {
        setUser(session.user);
      }

      const fileExt = file.name.split('.').pop()?.toLowerCase() || 'jpg';
      // RLS policy requires first folder to be auth.uid() - don't include 'avatars/' prefix
      // When using .from('avatars'), Supabase handles the bucket name
      const filePath = `${session.user.id}/${Date.now()}.${fileExt}`;

      // Determine content type — file.type can be empty on some browsers
      const mimeTypes: Record<string, string> = {
        jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png',
        gif: 'image/gif', webp: 'image/webp', heic: 'image/heic',
      };
      const contentType = file.type || mimeTypes[fileExt] || 'image/jpeg';

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, {
          contentType,
          cacheControl: '3600',
          upsert: true,
        });

      if (uploadError) {
        setError(`Failed to upload image: ${uploadError.message || 'Please try again'}`);
        setUploading(false);
        return;
      }

      const { data: urlData } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      if (urlData?.publicUrl) {
        setAvatarUrl(urlData.publicUrl);
        setProfileData({ avatarUrl: urlData.publicUrl });
        
        // Also update the database so it persists across steps
        const { data: { user: currentUser } } = await supabase.auth.getUser();
        if (currentUser) {
          await supabase
            .from('user_profiles')
            .update({ avatar_url: urlData.publicUrl })
            .eq('id', currentUser.id);
        }
      }
    } catch (err) {
      setError('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  }, [user, setProfileData]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      uploadFile(e.dataTransfer.files[0]);
    }
  }, [uploadFile]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      uploadFile(e.target.files[0]);
    }
  };

  const removeAvatar = () => {
    setAvatarUrl('');
    setProfileData({ avatarUrl: null });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Save avatar to store, mark as set, and proceed
    setProfileData({ avatarUrl: avatarUrl || null });
    setAvatarSet(true);
    onNext();
  };

  return (
    <div className="w-full max-w-lg mx-auto bg-[#221F1F] rounded-2xl p-6 border border-[#504A4A]">
      <div className="text-center mb-6">
        <div className="w-16 h-16 rounded-full bg-[#9B1421]/20 flex items-center justify-center mx-auto mb-4">
          <User size={32} className="text-text-primary" />
        </div>
        <h2 className="text-2xl font-bold text-[#EBFFEE]">Add Your Photo</h2>
        <p className="text-[#A9A3A3] mt-2">Upload a profile picture (optional)</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {avatarUrl ? (
          // Avatar Preview
          <div className="relative w-48 h-48 mx-auto">
            <img
              src={avatarUrl}
              alt="Profile"
              className="w-full h-full rounded-full object-cover border-4 border-[#504A4A]"
            />
            <button
              type="button"
              onClick={removeAvatar}
              className="absolute -top-2 -right-2 w-10 h-10 bg-red-600 hover:bg-red-700 rounded-full flex items-center justify-center text-white transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        ) : (
          // Upload Drop Zone
          <div
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            className={`relative border-2 border-dashed rounded-xl p-12 text-center transition-colors ${
              dragActive
                ? 'border-[#9B1421] bg-[#9B1421]/10'
                : 'border-[#504A4A] hover:border-[#9B1421]/50'
            }`}
          >
            <input
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              disabled={uploading}
            />
            <div className="flex flex-col items-center gap-4">
              {uploading ? (
                <>
                  <Loader2 size={48} className="text-rollops-accent animate-spin" />
                  <p className="text-[#EBFFEE]">Uploading...</p>
                </>
              ) : (
                <>
                  <div className="w-20 h-20 rounded-full bg-[#504A4A] flex items-center justify-center">
                    <Upload size={32} className="text-[#A9A3A3]" />
                  </div>
                  <div>
                    <p className="text-[#EBFFEE] font-medium">
                      Drop your photo here, or{' '}
                      <span className="text-rollops-accent">browse</span>
                    </p>
                    <p className="text-sm text-[#A9A3A3] mt-1">
                      PNG, JPG up to 5MB
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-3">
            <p className="text-red-400 text-sm text-center">{error}</p>
          </div>
        )}

        <p className="text-xs text-[#A9A3A3] text-center">
          Your photo helps others recognize you. You can always change this later.
        </p>

        <Button
          type="submit"
          disabled={uploading}
          variant="red"
          size="lg"
          className="w-full font-semibold"
        >
          {avatarUrl ? 'Continue' : 'Skip for Now'}
        </Button>
      </form>
    </div>
  );
}

